import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { goalIconFor } from "../goal_icon.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { IController } from "../../../webgl_2d/core/types/controller_types.js";

export interface NewGoalOverlayParams
{
  /** Block labels unlocking at the level about to be played, e.g. ["glass 1×2"]. */
  unlocks?: string[];
  onClose?: () => void;
}

export class NewGoalOverlay extends AbstractOverlay
{
  private static readonly APPEAR_DELAY_MS = 20;
  private static readonly APPEAR_MS = 190;
  private static readonly AUTO_CLOSE_MS = 2500;
  private close_callback_called = false;
  private glow_controller: IController | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: unknown,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    const unlocks = ( params as NewGoalOverlayParams | undefined )?.unlocks;
    const material = NewGoalOverlay.materialFrom( unlocks );
    const item_key = NewGoalOverlay.itemLocalizationKey( material );
    const description_key = NewGoalOverlay.descriptionLocalizationKey( material );

    const title_w = LayoutScaler.widthFromDesign( 820 );
    const title = new UITextElement(
      this.ctx,
      this.rect.getW2() - title_w / 2,
      this.rect.getH2() - LayoutScaler.heightFromDesign( 610 ),
      title_w,
      LayoutScaler.heightFromDesign( 170 ),
      {
        localization_key: "RESULT.NEW_ITEM",
        font_size: LayoutScaler.textSizeFromDesign( 104 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: "#F2B300",
        scale_to_width: true,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.addChild( title );

    const item_name = new UITextElement(
      this.ctx,
      this.rect.getW2() - title_w / 2,
      this.rect.getH2() - LayoutScaler.heightFromDesign( 410 ),
      title_w,
      LayoutScaler.heightFromDesign( 100 ),
      {
        localization_key: item_key,
        font_size: LayoutScaler.textSizeFromDesign( 66 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: 0,
        scale_to_width: true,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.addChild( item_name );

    const cube_size = LayoutScaler.widthFromDesign( 350 );
    const cube = new UIImage(
      this.ctx,
      this.rect.getW2() - cube_size / 2,
      this.rect.getH2() - LayoutScaler.heightFromDesign( 175 ),
      cube_size,
      cube_size,
      goalIconFor( unlocks )
    );
    this.addChild( cube );

    const cube_absolute = cube.getAbsolutePosition();

    const description = new UITextElement(
      this.ctx,
      this.rect.getW2() - LayoutScaler.widthFromDesign( 410 ),
      this.rect.getH2() + LayoutScaler.heightFromDesign( 230 ),
      LayoutScaler.widthFromDesign( 820 ),
      LayoutScaler.heightFromDesign( 180 ),
      {
        localization_key: description_key,
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: 0,
        auto_wrap: true,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.addChild( description );

    const close_hint = new UITextElement(
      this.ctx,
      this.rect.getW2() - LayoutScaler.widthFromDesign( 430 ),
      this.rect.getH2() + LayoutScaler.heightFromDesign( 690 ),
      LayoutScaler.widthFromDesign( 860 ),
      LayoutScaler.heightFromDesign( 90 ),
      {
        localization_key: "CLICK_TO_CLOSE",
        font_size: LayoutScaler.textSizeFromDesign( 42 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: 0,
        scale_to_width: true,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    close_hint.setOpacity( 1 );
    this.addChild( close_hint );

    const appear_at = performance.now() + NewGoalOverlay.APPEAR_DELAY_MS;
    title.setOpacity( 0 );
    title.setScale( new Vector2( 0.8, 0.8 ) );
    title.addTransform( new TransformOpacity( title, true, 0, 1, appear_at, NewGoalOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
    title.addTransform( new TransformScale( title, true, null, new Vector2( 1, 1 ), appear_at, NewGoalOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );

    item_name.setOpacity( 0 );
    item_name.addTransform( new TransformOpacity( item_name, true, 0, 1, appear_at + 50, NewGoalOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );

    cube.setOpacity( 0 );
    cube.setScale( new Vector2( 0.45, 0.45 ) );
    cube.addTransform( new TransformOpacity( cube, true, 0, 1, appear_at + 70, NewGoalOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
    cube.addTransform( new TransformScale( cube, true, null, new Vector2( 1, 1 ), appear_at + 70, NewGoalOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );

    description.setOpacity( 0 );
    description.addTransform( new TransformOpacity( description, true, 0, 1, appear_at + 130, NewGoalOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );

    const now = performance.now();
    this.glow_controller = Vfx2DManager.addCoinCounterGlow(
      new Vector2(
        cube_absolute.x + cube_size / 2,
        cube_absolute.y + cube_size / 2
      ),
      LayoutScaler.widthFromDesign( 220 ),
      performance.now(),
      1000,
      true,
      180,
      true,
      0.35
    );
    Vfx2DManager.addConfettiBottom( now );
    Vfx2DManager.addConfettiSide( now + 80 );

    setTimeout( () =>
    {
      if ( OverlayManager.isOverlayOpen( OverlayId.NEW_GOAL_OVERLAY ) )
        OverlayManager.closeOverlay();
    }, NewGoalOverlay.AUTO_CLOSE_MS );
  }

  private static materialFrom( unlocks: string[] | undefined ): string
  {
    for ( const label of unlocks ?? [] )
    {
      const raw_material = String( label ).trim().split( " " )[0].toLowerCase();
      switch ( raw_material )
      {
        case "brick": return "brick";
        case "glass":
        case "jar": return "jar";
        case "stone": return "stone";
        case "ice": return "ice";
        case "iron":
        case "metal": return "metal";
        case "tree":
        case "log": return "log";
        case "plastic": return "plastic";
        case "column": return "column";
        case "rotating_table": return "rotating_table";
        case "side_to_side_table": return "side_to_side_table";
        case "up_and_down_table": return "up_and_down_table";
        case "tilted_table": return "tilted_table";
        case "fan": return "fan";
        case "hammer": return "hammer";
      }
    }
    return "box";
  }

  private static itemLocalizationKey( material: string ): string
  {
    switch ( material )
    {
      case "brick": return "BLOCK.BRICK";
      case "jar": return "BLOCK.JAR";
      case "stone": return "BLOCK.STONE";
      case "ice": return "BLOCK.ICE";
      case "metal": return "BLOCK.METAL";
      case "plastic": return "BLOCK.PLASTIC";
      case "column": return "BLOCK.COLUMN";
      case "rotating_table": return "ROTATING_TABLE";
      case "side_to_side_table": return "SIDE_TO_SIDE_TABLE";
      case "up_and_down_table": return "UP_AND_DOWN_TABLE";
      case "tilted_table": return "TILTED_TABLE";
      case "fan": return "BARRIER.FAN";
      case "hammer": return "BARRIER.HAMMER";
      // There is currently no separate BLOCK.LOG key in the supplied title
      // list, so logs keep the generic block title and use their own description.
      case "log": return "BLOCK.BOX";
      default: return "BLOCK.BOX";
    }
  }

  private static descriptionLocalizationKey( material: string ): string
  {
    switch ( material )
    {
      case "brick": return "BRICK.DESCRIPTION";
      case "ice": return "ICE.DESCRIPTION";
      case "jar": return "JAR.DESCRIPTION";
      case "log": return "LOG.DESCRIPTION";
      case "metal": return "METAL.DESCRIPTION";
      case "stone": return "STONE.DESCRIPTION";
      case "plastic": return "PLASTIC.DESCRIPTION";
      case "column": return "COLUMN.DESCRIPTION";
      case "rotating_table": return "ROTATING_TABLE.DESCRIPTION";
      case "side_to_side_table": return "SIDE_TO_SIDE_TABLE.DESCRIPTION";
      case "up_and_down_table": return "UP_AND_DOWN_TABLE.DESCRIPTION";
      case "tilted_table": return "TILTED_TABLE.DESCRIPTION";
      case "fan": return "BARRIER.FAN.DESCRIPTION";
      case "hammer": return "BARRIER.HAMMER.DESCRIPTION";
      default: return "BOX.DESCRIPTION";
    }
  }

  public name(): string
  {
    return "NewGoalOverlay";
  }

  public override onOverlayWillClose(): void
  {
    this.glow_controller?.stop();
    this.glow_controller = null;

    if ( this.close_callback_called )
      return;

    this.close_callback_called = true;
    ( this.params as { onClose?: () => void } | undefined )?.onClose?.();
  }

  public override onTouchEnd( p: Vector2, now: number ): void
  {
    super.onTouchEnd( p, now );
    OverlayManager.closeOverlay();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
